import { filter, isEmpty, map, mapValues, orderBy, some } from 'es-toolkit/compat';

import { Orchestration } from 'api/experimentResults';
import { ResultsMode } from 'interfaces/experimentResults';
import { Study } from 'interfaces/study';
import { useOrchestrations } from 'utils/queryHooks/useOrchestrations';
import { useJobsByOrchestrationIds } from 'utils/queryHooks/jobs/useJobs';
import { usePermissions } from 'utils/usePermissions';
import { Permission } from 'interfaces/permissionOption';
import { useWorkflowExportSourceUrlState } from 'services/export/useResultsExportUrlState';
import {
  getNoWorkflowResultsNote,
  getWorkflowSourceAvailability,
  hasWorkflowResults,
  isWorkflowPostProcessingResult,
  resolveWorkflowResultsSource,
  toWorkflowResultsModeParams,
  WorkflowExportKind,
  WorkflowResultsSourceSelection,
  workflowResultsCheckingMessage,
  WorkflowSourceAvailability,
} from 'services/export/workflowExportSource';
// Owns the results source of one Workflow export dialog. The user's choice lives in the dialog's URL state; until
// there is one, the source is Approved results when there are any, else Published, else Each slide's latest run.
// The Workflow tab's run is not copied in. A specific-run choice does not name study-wide runs; each panel does.
// Users who may not view unpublished results are always on Published.
// `availability` is the whole dialog's verdict: still checking which results exist (nothing may be chosen or
// exported yet), nothing to export at all, or the selected source's own availability.
export const useWorkflowResultsSource = (
  kind: WorkflowExportKind,
  study?: Study,
  options?: { ignoreStoredChoice?: boolean; enabled?: boolean }
) => {
  const { hasPermission } = usePermissions();
  const canViewUnpublishedResults = hasPermission(Permission.ViewUnpublishedResults);
  const urlState = useWorkflowExportSourceUrlState();
  const ignoreStoredChoice = Boolean(options?.ignoreStoredChoice);
  const enabled = options?.enabled !== false;

  const { data, isInitialLoading, isFetching, isError, error } = useOrchestrations({
    studyId: study?.id ?? '',
    resultsMode: ResultsMode.Manual,
    enabled: enabled && canViewUnpublishedResults,
  });
  const allRuns: Orchestration[] = data?.orchestrations ?? [];
  // Features and cell tables export the same post-processing runs. A newer normalization run is not a result.
  const runs = orderBy(
    filter(allRuns, (run) => some(run.slidesData, (slide) => isWorkflowPostProcessingResult(slide.flowClassName))),
    ['createdAtMinimum'],
    ['desc']
  );

  const knownRunIds = map(runs, 'orchestrationId');
  const { jobsByOrchestrationId, isLoading: isLoadingRunNames } = useJobsByOrchestrationIds({
    studyId: study?.id ?? '',
    orchestrationIds: knownRunIds,
    enabled: canViewUnpublishedResults && !isEmpty(knownRunIds),
  });
  const runNames = mapValues(jobsByOrchestrationId, (job) => job?.name);
  const chosen: WorkflowResultsSourceSelection | undefined =
    !ignoreStoredChoice && urlState.source ? { source: urlState.source } : undefined;
  const selection = study
    ? resolveWorkflowResultsSource({
        kind,
        study,
        canViewUnpublishedResults,
        chosen,
        runCount: runs.length,
        isLoadingRuns: isInitialLoading,
      })
    : ({ source: 'published' } as WorkflowResultsSourceSelection);

  const hasNoResults =
    Boolean(study?.allowExport) &&
    canViewUnpublishedResults &&
    !isInitialLoading &&
    !isError &&
    !hasWorkflowResults({ kind, study: study as Study, runCount: runs.length, canViewUnpublishedResults });
  const availability: WorkflowSourceAvailability = !study?.allowExport
    ? { disabled: true, note: 'Exports are disabled for this study.' }
    : isInitialLoading
    ? { disabled: true, note: workflowResultsCheckingMessage }
    : hasNoResults
    ? { disabled: true, note: getNoWorkflowResultsNote(kind) }
    : getWorkflowSourceAvailability({
        kind,
        selection,
        study,
        runCount: runs.length,
        canViewUnpublishedResults,
      });

  return {
    canViewUnpublishedResults,
    selection,
    select: urlState.select,
    setPanelRunSelections: urlState.setPanelRunSelections,
    panelRunSelections: urlState.panelRunSelections,
    clear: urlState.clear,
    runs,
    ignoredStudyRunIds: urlState.ignoredStudyRunIds,
    isLoadingRuns: isInitialLoading,
    // A refresh of an already listed set of runs: the list is usable but may be about to change.
    isRefreshingRuns: isFetching && !isInitialLoading,
    // The run options also wait for the job names, so a run is never listed by its id while they load.
    isLoadingRunOptions: isInitialLoading || isLoadingRunNames,
    runNames,
    hasRunsError: isError,
    runsError: error,
    // Study flags establish published results without the orchestration list. A failed list must not erase them.
    publishedResultsEstablished: Boolean(study && (kind === 'features' ? study.hasFeatures : study.hasResults)),
    hasNoResults,
    availability,
    modeParams: toWorkflowResultsModeParams(selection, canViewUnpublishedResults),
  };
};
